import { useId, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { ratioString, scaleHeight } from "../lib/ratio";

/** Locale-independent number formatting (deterministic for SSR). */
function fmt(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/\.?0+$/, "");
}

export function AspectRatioTool() {
  const uid = useId();
  const [width, setWidth] = useState("1920");
  const [height, setHeight] = useState("1080");
  const [targetWidth, setTargetWidth] = useState("1280");

  const ratio = useMemo(
    () => ratioString(Number(width), Number(height)),
    [width, height],
  );
  const targetHeight = useMemo(
    () => scaleHeight(Number(targetWidth), Number(width), Number(height)),
    [targetWidth, width, height],
  );

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-w`}>{m.ar_width_label()}</Label>
            <Input
              id={`${uid}-w`}
              type="text"
              inputMode="numeric"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              autoComplete="off"
              className="font-mono text-lg"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-h`}>{m.ar_height_label()}</Label>
            <Input
              id={`${uid}-h`}
              type="text"
              inputMode="numeric"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              autoComplete="off"
              className="font-mono text-lg"
            />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border bg-card/40 px-4 py-3">
          <span className="text-sm text-muted-foreground">
            {m.ar_ratio_label()}
          </span>
          <span
            data-testid="ar-ratio"
            className="font-mono text-2xl font-bold text-foreground"
          >
            {ratio ?? "—"}
          </span>
        </div>
      </div>

      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <h2 className="font-display text-lg font-bold tracking-tight">
          {m.ar_resize_title()}
        </h2>
        <div className="grid items-end gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-tw`}>{m.ar_target_width_label()}</Label>
            <Input
              id={`${uid}-tw`}
              type="text"
              inputMode="numeric"
              value={targetWidth}
              onChange={(e) => setTargetWidth(e.target.value)}
              autoComplete="off"
              className="font-mono text-lg"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-th`}>{m.ar_target_height_label()}</Label>
            <Input
              id={`${uid}-th`}
              readOnly
              data-testid="ar-newheight"
              value={targetHeight !== null ? fmt(targetHeight) : ""}
              className="font-mono text-lg"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
